import React from 'react';
import { Input } from '@avila-tek/ui/src';
import { ACTIONS } from '../reducer';
import { ExcelForm } from '../../shared';
import { ExcelDocument, Variant } from '../../../models';
import { useCreateProductAndVariant } from '../../../hooks';
import CodeForm from '../code/CodeForm';

interface VariantInfoProps {
  setFormVariant: React.Dispatch<React.SetStateAction<Partial<Variant>>>;
}

function VariantInfo({ setFormVariant }: VariantInfoProps) {
  const {
    // States
    formState,
    setFormState,
    addNewCodes,
    codesArray,
    setCodesArray,
    isOnlineState,
    expirationDate,
    // Handlers functions
    handleChangeString,
    handleChangeNumber,
    handleExpitationDateInputChange,
    handleSetIsOnline,
    handleChangeAddNewCodes,
    handleExcelDocument,
    handleExpirationDateInputChange,
  } = useCreateProductAndVariant();

  React.useEffect(() => {
    if (addNewCodes) {
      setFormState({
        ...formState,
        stock: codesArray.length,
        electronicCodes: codesArray.map((code) => ({
          code,
          ...{ expirationDate: formState.expirationDate || null },
        })),
      });
    }
  }, [codesArray, formState.expirationDate]);

  React.useEffect(() => {
    setFormState({
      ...formState,
      isOnline: isOnlineState,
    });
  }, [isOnlineState]);

  React.useEffect(() => {
    setFormVariant({
      ...formState,
    });
  }, [formState]);

  const inputContainerClass = 'px-5 py-1 w-full mt-5';

  return (
    <>
      <div className="flex justify-center text-center border-b py-1 text-gray-700 bg-gray-100">
        Inventario
      </div>
      <div className="w-full relative p-5 grid md:grid-cols-2 justify-center items-center">
        <div className="absolute top-[10px] right-[230px] space-x-2 mr-[6px]">
          <span>Compra online</span>
          <input
            type="checkbox"
            name={ACTIONS.IS_ONLINE}
            checked={formState.isOnline}
            onChange={handleSetIsOnline}
            className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
          />
        </div>
        <div className="absolute top-[10px] right-10 space-x-2 mr-[6px]">
          <span>Códigos de descuento</span>
          <input
            type="checkbox"
            checked={addNewCodes}
            onChange={handleChangeAddNewCodes}
            className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
          />
        </div>
        <div className={inputContainerClass}>
          <Input
            required
            name={ACTIONS.PRICE}
            label="Precio"
            type="number"
            step="0.01"
            value={formState.price}
            placeholder="Ingrese un precio"
            onChange={handleChangeNumber}
          />
        </div>
        <div className={inputContainerClass}>
          <Input
            required
            name={ACTIONS.COMPARE_AT_PRICE}
            label="Precio comparativo"
            type="number"
            min={formState.compareAtPrice}
            step="0.01"
            value={formState.compareAtPrice}
            placeholder="Ingrese un monto"
            onChange={handleChangeNumber}
          />
        </div>
        <div className={inputContainerClass}>
          <Input
            name={ACTIONS.AGREED_PRICE}
            label="Precio (acordado con aliado)"
            type="number"
            step="0.01"
            value={formState.agreedPrice}
            placeholder="Ingrese un precio acordado"
            onChange={handleChangeNumber}
          />
        </div>
        <div className={inputContainerClass}>
          <Input
            name={ACTIONS.SKU}
            label="SKU"
            type="text"
            value={formState.sku}
            placeholder="Ingrese el SKU"
            onChange={handleChangeString}
          />
        </div>
        {!addNewCodes ? (
          <div className={inputContainerClass}>
            <Input
              name={ACTIONS.STOCK}
              label="Stock"
              type="text"
              value={formState.stock}
              placeholder="Ingrese stock"
              onChange={handleChangeNumber}
            />
          </div>
        ) : null}
      </div>
      {addNewCodes ? (
        <>
          <div className="w-[100%] border-t">
            <h1 className="px-10 py-5 text-lg text-neutral-400">
              Información de códigos{' '}
              <span className="text-[10px]">no autogenerados</span>
            </h1>
          </div>
          <div className="w-full relative px-5 grid md:grid-cols-1 justify-start items-start">
            <div className="w-full px-5 ">
              <div className="flex flex-col justify-center items-center">
                <div className="w-full">
                  <ExcelForm
                    sheetName="Codigos"
                    updateData={handleExcelDocument}
                    documentFormat="https://avilatek.sfo3.digitaloceanspaces.com/images/Formato_Codes_Meit.xlsx"
                  />
                </div>
                <div className={`w-full  ${addNewCodes ? 'px-14 py-4' : ''}`}>
                  {/* Seccion de codigos especiales */}
                  {codesArray.length > 0 && addNewCodes ? (
                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 justify-center items-center">
                      {codesArray.map((code, index) => (
                        <CodeForm
                          codeInfo={code}
                          key={index}
                          setCodesArray={setCodesArray}
                          index={index}
                        />
                      ))}
                    </section>
                  ) : null}
                  {codesArray.length === 0 && addNewCodes ? (
                    <div className="flex flex-col justify-center items-center">
                      <h1 className="text-danger-200">
                        Agregue un archivo excel para poder ver los códigos
                      </h1>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className=" w-full p-5 space-y-2">
              {/* EXPIRATION DATE */}
              <div className="space-x-2 mr-[6px]">
                <span>Fecha de vencimiento</span>
                <input
                  type="checkbox"
                  name="isOnline"
                  checked={expirationDate}
                  onChange={handleExpirationDateInputChange}
                  className="w-4 h-4 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
                />
              </div>
              {expirationDate && expirationDate ? (
                <div className="w-full">
                  <Input
                    name="expirationDate"
                    value={
                      formState.expirationDate
                        ? formState.expirationDate.toISOString().split('T')[0]
                        : ''
                    }
                    type="date"
                    placeholder="Ingrese una fecha"
                    onChange={handleExpitationDateInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

export default VariantInfo;
